import React, { useState } from 'react';
import { apiService } from '../services/apiService';
import type { FeedUploadResult, FeedInspectResult } from '../services/apiService';
import { FEED_MAP_FIELDS, isRequiredFor, missingRequiredFields } from '../utils/feedColumns';

interface FeedIssue {
  reason: string;
  severity: 'error' | 'warning';
  count: number;
  sampleRows?: number[];
}
interface FeedRowIssue { row: number; severity: 'error' | 'warning'; reason: string; }

// Grouped issue list, each group expandable to every affected line number so
// the publisher can fix their CSV line by line
const IssueList: React.FC<{ issues: FeedIssue[]; rowIssues: FeedRowIssue[]; truncated?: boolean }> =
  ({ issues, rowIssues, truncated }) => {
    const [expanded, setExpanded] = useState<string | null>(null);
    if (!issues || issues.length === 0) return null;

    return (
      <div data-testid="upload-issues" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
        {issues.map((issue) => {
          const lines = rowIssues.filter(r => r.reason === issue.reason).map(r => r.row);
          const isExpanded = expanded === issue.reason;
          return (
            <div key={issue.reason} style={{ marginBottom: '0.35rem' }}>
              <span>{issue.severity === 'error' ? '❌' : '⚠️'} </span>
              <strong>{issue.count} row{issue.count === 1 ? '' : 's'}</strong>: {issue.reason}
              {lines.length > 0 && (
                <>
                  {' '}<button
                    type="button"
                    onClick={() => setExpanded(isExpanded ? null : issue.reason)}
                    style={{ border: 'none', background: 'none', color: 'var(--brand)', cursor: 'pointer', padding: 0, fontSize: '0.85em' }}
                  >
                    {isExpanded ? 'hide lines' : `show lines (${issue.sampleRows?.slice(0, 3).join(', ')}${issue.count > 3 ? '…' : ''})`}
                  </button>
                  {isExpanded && (
                    <div data-testid="issue-lines" style={{ margin: '0.25rem 0 0 1.4rem', fontFamily: 'monospace', fontSize: '0.85em' }}>
                      lines {lines.join(', ')}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
        {truncated && (
          <div style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
            Line detail truncated — counts above cover the whole file.
          </div>
        )}
      </div>
    );
  };

const SUPPORTED_NETWORKS = [
  { value: 'amazon', label: 'Amazon-format CSV (prepared separately)' },
  { value: 'generic', label: 'Generic CSV (required fields)' },
  { value: 'shareasale', label: 'ShareASale legacy export (network closed)' },
  { value: 'cj', label: 'Commission Junction (CJ)' },
  { value: '2performant', label: '2Performant' },
  { value: 'awin', label: 'Awin' },
  { value: 'rakuten', label: 'Rakuten Advertising' },
  { value: 'impact', label: 'Impact' },
];

// Mirrors the server cap (packages/api/src/services/feed-limits.ts
// FEED_MAX_BYTES) — a local const because that module is server-only
// (imports node:zlib) and must not enter the CRA bundle.
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MiB

const GZIP_MIME_TYPES = ['application/gzip', 'application/x-gzip'];

// .csv, .csv.gz (or plain .gz), or a browser-reported CSV/gzip MIME type.
// The server does the authoritative content check (gzip magic bytes).
function isAcceptedFeedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    name.endsWith('.csv') ||
    name.endsWith('.gz') ||
    file.type === 'text/csv' ||
    GZIP_MIME_TYPES.includes(file.type)
  );
}

interface FeedUploadFormProps {
  onUploadSuccess: () => void;
}

const FeedUploadForm: React.FC<FeedUploadFormProps> = ({ onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [network, setNetwork] = useState('');
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<FeedUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Column review (feed-column-mapping): a file is inspected before it is
  // imported, so the publisher can see and correct what we think each column
  // means rather than discovering it from a failed upload.
  const [inspecting, setInspecting] = useState(false);
  const [inspection, setInspection] = useState<FeedInspectResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [linkConfirmed, setLinkConfirmed] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setResult(null);
    setError(null);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!isAcceptedFeedFile(file)) {
      setError('Only .csv or gzipped .csv.gz files are supported');
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds the 25 MiB limit — gzip the feed (.csv.gz) or split it');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    void inspectFile(file);
  };

  // Ask the API what the columns look like. A recognised export needs no
  // choices from the publisher; anything else surfaces the review step.
  const inspectFile = async (file: File, forNetwork?: string) => {
    const net = forNetwork ?? network;
    if (!net) return; // no network chosen yet — re-inspected when one is
    setInspecting(true);
    setInspection(null);
    setMapping({});
    setLinkConfirmed(false);
    try {
      const found = await apiService.inspectFeed(file, net);
      setInspection(found);
      setMapping({ ...(found.mapping || {}) });
      // Nothing was ambiguous, but the affiliate link is confirmed every time
      // regardless — see the note by the checkbox.
      setLinkConfirmed(false);
    } catch (err) {
      // Inspection is an aid, not a gate: if it fails the publisher can still
      // upload and get the server's normal validation.
      setInspection(null);
    } finally {
      setInspecting(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !network) return;

    setUploading(true);
    setResult(null);
    setError(null);

    try {
      const uploadResult = await apiService.uploadFeed(selectedFile, network, label.trim() || undefined, mapping);
      setResult(uploadResult);

      if (uploadResult.success) {
        setSelectedFile(null);
        setNetwork('');
        setLabel('');
        setInspection(null);
        setMapping({});
        setLinkConfirmed(false);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        onUploadSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again later.');
    } finally {
      setUploading(false);
    }
  };

  const missingRequired = inspection ? missingRequiredFields(mapping, network) : [];
  // The affiliate link is confirmed on every upload, however it was resolved.
  // Mapping the merchant's own product URL instead of the tracked link passes
  // every validation, imports, renders, converts — and earns nothing, with no
  // error anywhere. A glance at the sample value is the only thing that catches it.
  const needsLinkConfirm = !!inspection && !linkConfirmed;

  // WHY UPLOAD IS UNAVAILABLE — ONE LIST, WHICH IS BOTH THE GATE AND THE REASON.
  //
  // There are six ways to be blocked and the screen used to name ONE of them.
  // Driven at 390 on the ordinary path: file chosen with no network yet, and
  // file + network with the link not yet ticked, both render a greyed "Upload
  // Feed" and no sentence anywhere on the page. The only one that spoke was
  // "Still needed: …", which a publisher reaches by UNSETTING a column the server
  // had already resolved — the state they are least likely to be stuck in.
  //
  // Two of the six already have their own words on screen (`Reading your file…`
  // and `Still needed: …`), so they carry `say: null` rather than a second
  // sentence saying the same thing twice.
  //
  // The list is the gate as well, deliberately: `canUpload` was a six-clause
  // conjunction and a separate sentence explaining it would be that rule stated
  // twice — which in this repo is how the two eventually disagree, and the one
  // that disagrees is the one nobody is testing.
  //
  // ORDER IS THE ORDER THE PUBLISHER MUST RESOLVE THEM, not the order they were
  // written: network first, because the file cannot even be inspected without it.
  const blockers: Array<{ when: boolean; say: string | null }> = [
    { when: !network, say: 'Choose the affiliate network this file came from.' },
    { when: !selectedFile, say: 'Choose a CSV file to upload.' },
    { when: inspecting, say: null },
    { when: missingRequired.length > 0, say: null },
    { when: needsLinkConfirm, say: 'Tick “This is my tracked affiliate link” above to upload.' },
    { when: uploading, say: null },
  ];
  const blocking = blockers.find(b => b.when) ?? null;
  const canUpload = blocking === null;

  return (
    <div className="upload-section">
      <h3>Upload New Feed</h3>
      <div className="upload-form" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px' }}>
        <div>
          <label htmlFor="network-select" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
            Affiliate Network
          </label>
          <select
            id="network-select"
            data-testid="feed-network"
            value={network}
            onChange={(e) => {
              const next = e.target.value;
              setNetwork(next);
              // Publishers commonly pick the file first. Re-inspect on network
              // change so the review step appears either way round.
              setInspection(null);
              setMapping({});
              setLinkConfirmed(false);
              if (selectedFile && next) void inspectFile(selectedFile, next);
            }}
            disabled={uploading}
            style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid var(--line)' }}
          >
            <option value="">Select a network...</option>
            {SUPPORTED_NETWORKS.map((n) => (
              <option key={n.value} value={n.value}>{n.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="feed-label" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
            Feed Label <span style={{ fontWeight: 'normal', color: 'var(--muted)' }}>(optional)</span>
          </label>
          <input
            id="feed-label"
            data-testid="feed-label"
            type="text"
            value={label}
            maxLength={60}
            placeholder="advertiser name, e.g. outdoor-gear"
            onChange={(e) => setLabel(e.target.value)}
            disabled={uploading}
            style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid var(--line)', boxSizing: 'border-box' }}
          />
          <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--muted)' }}>
            Running several advertisers on one network? Give each feed a label. Re-uploading to the
            same label <strong>replaces</strong> that feed's products.
          </div>
        </div>

        <div>
          <label htmlFor="file-input" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
            CSV File (.csv or .csv.gz, max 25 MiB)
          </label>
          {/* Reachable at the moment of confusion, rather than only after a
              failed upload — the format was previously undocumented anywhere. */}
          <div style={{ marginBottom: '0.4rem', fontSize: '0.85rem', color: 'var(--muted)' }}>
            Not sure what your file needs?{' '}
            <a
              href="https://imgaff.com/feed-format.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feed-format-guide-link"
            >
              What your product feed needs
            </a>
          </div>
          {/* No `accept` filter: Android Chrome grays out files whose
              MIME type it can't match, and file managers routinely report
              .csv / .csv.gz as application/octet-stream (or no type),
              making valid feeds unpickable on mobile. handleFileSelect's
              isAcceptedFeedFile check (extension OR MIME) is the real gate,
              and the server does the authoritative content check. */}
          <input
            id="file-input"
            data-testid="feed-file"
            type="file"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          {selectedFile && (
            <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: 'var(--muted)' }}>
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>

        {inspecting && (
          <div data-testid="inspecting" style={{ fontSize: '0.9rem', color: 'var(--muted)' }}>
            Reading your file…
          </div>
        )}

        {inspection && (
          <div
            data-testid="column-review"
            style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: '0.9rem' }}
          >
            {/* When everything resolved, this panel is a CONFIRMATION, not a
                task — and it must read that way. The owner, who built this,
                still described it as "asking me to choose the columns" on a
                feed we parsed perfectly (8,141/8,141 products). If the person
                who wrote it reads a success as a failure, a new publisher
                certainly will, and this is the first screen of onboarding.
                The one thing genuinely worth a human glance is the affiliate
                link: the wrong column there costs money silently, with no
                error anywhere — which is why the API always returns it in
                mustConfirm. Say that, rather than implying nothing worked. */}
            <div style={{ fontWeight: 'bold', marginBottom: '0.15rem' }}>
              {inspection.autoResolved ? 'Columns recognised' : 'Check your columns'}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.7rem' }}>
              {inspection.autoResolved
                ? 'We read your feed and matched every column automatically. Just confirm the affiliate link below — it is the one we always double-check, because the wrong column would send clicks that earn you nothing.'
                : 'We could not recognise every column. Pick the right one for anything marked below.'}
            </div>

            <table className="column-map" style={{ fontSize: '0.875rem' }}>
              <tbody>
                {FEED_MAP_FIELDS.map((f) => {
                  const field = f.key;
                  const required = isRequiredFor(f, network);
                  const chosen = mapping[field] || '';
                  const sample = chosen ? inspection.samples?.[field] : undefined;
                  if (!required && !chosen) return null; // keep optional noise out
                  return (
                    <tr key={field} data-testid={`map-row-${field}`}>
                      <td>
                        {f.label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}
                      </td>
                      <td>
                        <select
                          data-testid={`map-select-${field}`}
                          value={chosen}
                          disabled={uploading}
                          onChange={(e) => {
                            setMapping(prev => ({ ...prev, [field]: e.target.value }));
                            if (field === 'link') setLinkConfirmed(false);
                          }}
                        >
                          <option value="">— not set —</option>
                          {inspection.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                      <td
                        data-testid={`map-sample-${field}`}
                        className="column-map-sample"
                        title={sample || ''}
                      >
                        {sample || ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <label style={{ display: 'block', marginTop: '0.7rem', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                data-testid="confirm-link"
                checked={linkConfirmed}
                disabled={uploading}
                onChange={(e) => setLinkConfirmed(e.target.checked)}
              />{' '}
              This is my tracked affiliate link
              {mapping.link && inspection.samples?.link && (
                <span style={{ color: 'var(--muted)' }}> — {inspection.samples.link}</span>
              )}
            </label>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.2rem' }}>
              If this is the shop's own product page rather than your tracked link, clicks will
              work but you will not earn commission.{' '}
              <a href="https://imgaff.com/feed-format.html" target="_blank" rel="noopener noreferrer">
                More about feed columns
              </a>
            </div>

            {missingRequired.length > 0 && (
              <div data-testid="missing-required" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--danger)' }}>
                Still needed: {missingRequired.map(f => f.label).join(', ')}
              </div>
            )}
          </div>
        )}

        <div>
          <button
            onClick={handleUpload}
            disabled={!canUpload}
            data-testid="upload-feed-button"
            className="upload-btn"
          >
            {uploading ? 'Uploading...' : 'Upload Feed'}
          </button>
          {blocking?.say && (
            <p
              data-testid="upload-blocked"
              role="status"
              style={{ margin: '0.4rem 0 0', fontSize: '0.85rem', color: 'var(--muted)' }}
            >
              {blocking.say}
            </p>
          )}
        </div>
      </div>

      {error && (
        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--danger-bg)', borderRadius: 'var(--radius)', color: 'var(--danger)' }}>
          <strong>Error:</strong> {error}
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>
            Check your CSV format and try again. Ensure the file contains the required columns for your selected network.
          </p>
        </div>
      )}

      {result && result.success && result.result && (
        <div data-testid="upload-status" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--good-bg)', borderRadius: 'var(--radius)', color: 'var(--good)' }}>
          <strong>Upload successful!</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            {result.result.replaced ? 'Replaced' : 'Created'} feed
            {result.result.feedLabel ? <strong> '{result.result.feedLabel}'</strong> : null} —
            imported {result.result.processedProducts} of {result.result.totalRows ?? result.result.totalProducts} rows
            {result.result.skippedProducts > 0 && (
              <span> ({result.result.skippedProducts} skipped)</span>
            )}
          </p>
          {(result.result as any).issues?.length > 0 ? (
            <IssueList
              issues={(result.result as any).issues}
              rowIssues={(result.result as any).rowIssues || []}
              truncated={(result.result as any).rowIssuesTruncated}
            />
          ) : result.result.errors && result.result.errors.length > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
              <strong>Warnings:</strong>
              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                {result.result.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {result && !result.success && (
        <div data-testid="upload-status" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--danger-bg)', borderRadius: 'var(--radius)', color: 'var(--danger)' }}>
          {(result.result as any)?.aborted ? (
            <>
              <strong>Upload refused — your previous products are unchanged.</strong>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>
                Most of this file failed validation. Double-check that you selected the right
                network and that the file is a product feed export.
              </p>
            </>
          ) : (
            <>
              <strong>Upload failed:</strong> {result.message}
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>
                Check your CSV format and try again. Ensure the file contains the required columns for your selected network.
              </p>
            </>
          )}
          {(result.result as any)?.issues?.length > 0 && (
            <IssueList
              issues={(result.result as any).issues}
              rowIssues={(result.result as any).rowIssues || []}
              truncated={(result.result as any).rowIssuesTruncated}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default FeedUploadForm;
